import React, { useState, useMemo } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import SEO from '../components/SEO';
import Header from '../components/Header';
import Footer from '../components/Footer';
import '../styles/ProForma.css';

const DEFAULTS = {
  // Acquisition & financing
  occupancy: 'buy', // 'buy' | 'lease'
  purchase: 5500000,
  buildout: 800000,
  downPct: 10,
  rate: 7,
  termYears: 25,
  leaseRatePerSqft: 25,
  buildingSqft: 19156,
  // Revenue drivers
  strengthMembers: 250,
  strengthDues: 99,
  courts: 2,
  courtRate: 70,
  courtHrs: 40,
  courtWeeks: 50,
  programmingRev: 260000,
  coffeeRev: 320000,
  coffeeCogsPct: 50,
  sandTurfRev: 70000,
  flagRev: 45000,
  eventsRev: 55000,
  retailRev: 25000,
  retailCogsPct: 55,
  // Operating costs
  payroll: 450000,
  utilities: 70000,
  insurance: 35000,
  marketing: 40000,
  maintenance: 45000,
  otherOpex: 105000,
  // Economic impact assumptions
  impactEvents: 12,
  impactVisitorsPerEvent: 200,
  impactStayDays: 1.5,
  impactSpendPerDay: 90,
  impactMultiplier: 1.6,
  memberVisitsPerWeek: 2,
  programParticipantsPerWeek: 350,
  programWeeks: 40,
  cafeAvgTicket: 8,
};

const money = (n) => (n < 0 ? '-$' : '$') + Math.round(Math.abs(n)).toLocaleString('en-US');
const moneyK = (n) => (n < 0 ? '-$' : '$') + Math.round(Math.abs(n) / 1000).toLocaleString('en-US') + 'k';
const moneyCompact = (n) => {
  const a = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (a >= 1000000) return `${sign}$${(a / 1000000).toFixed(1)}M`;
  if (a >= 1000) return `${sign}$${Math.round(a / 1000)}K`;
  return money(n);
};
const count = (n) => Math.round(n).toLocaleString('en-US');
const pct = (n) => `${(n * 100).toFixed(1)}%`;

const PIE_COLORS = ['#e0301e', '#1677ff', '#16a34a', '#9333ea', '#f59e0b', '#0891b2', '#db2777', '#64748b'];

function Field({ label, name, value, onChange, prefix, suffix }) {
  return (
    <label className="pf-field">
      <span className="pf-field-label">{label}</span>
      <span className="pf-input-wrap">
        {prefix && <span className="pf-affix">{prefix}</span>}
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(name, e.target.value === '' ? 0 : Number(e.target.value))}
        />
        {suffix && <span className="pf-affix pf-affix-suffix">{suffix}</span>}
      </span>
    </label>
  );
}

function ProForma() {
  const [v, setV] = useState(DEFAULTS);
  const set = (name, val) => setV((prev) => ({ ...prev, [name]: val }));
  const reset = () => setV(DEFAULTS);

  const m = useMemo(() => {
    const isLease = v.occupancy === 'lease';
    // Capital that needs financing: build-out always; building purchase only when buying.
    const financed = isLease ? v.buildout : v.purchase + v.buildout;
    const equity = financed * (v.downPct / 100);
    const loanPrincipal = financed - equity;
    const r = v.rate / 100 / 12;
    const n = v.termYears * 12;
    const monthly =
      loanPrincipal <= 0 ? 0 : r === 0 ? loanPrincipal / n : (loanPrincipal * r) / (1 - Math.pow(1 + r, -n));
    const annualDebt = monthly * 12;
    const annualRent = isLease ? v.leaseRatePerSqft * v.buildingSqft : 0;
    const occupancyCost = annualDebt + annualRent;
    const projectCost = isLease ? v.buildout : v.purchase + v.buildout;

    const strengthRev = v.strengthMembers * v.strengthDues * 12;
    const courtRev = v.courts * v.courtRate * v.courtHrs * v.courtWeeks;
    const grossRevenue =
      strengthRev + courtRev + v.programmingRev + v.coffeeRev + v.sandTurfRev + v.flagRev + v.eventsRev + v.retailRev;
    const cogs = v.coffeeRev * (v.coffeeCogsPct / 100) + v.retailRev * (v.retailCogsPct / 100);
    const grossMargin = grossRevenue - cogs;
    const opex = v.payroll + v.utilities + v.insurance + v.marketing + v.maintenance + v.otherOpex;
    const ebitda = grossMargin - opex;
    const cashFlow = ebitda - occupancyCost;
    const ebitdaMargin = grossRevenue > 0 ? ebitda / grossRevenue : 0;
    const coverage = occupancyCost > 0 ? ebitda / occupancyCost : 0;
    const cashOnCash = equity > 0 ? cashFlow / equity : 0;

    const ramp = [0.65, 0.85, 1.0].map((f, i) => {
      const rev = grossRevenue * f;
      const c = cogs * f;
      const eb = rev - c - opex;
      return { year: `Year ${i + 1}`, EBITDA: Math.round(eb), 'Cash Flow': Math.round(eb - occupancyCost) };
    });

    const revenueMix = [
      { name: 'Strength memberships', value: strengthRev },
      { name: 'Court rentals', value: courtRev },
      { name: 'Youth/family programming', value: v.programmingRev },
      { name: 'TMRW Coffee', value: v.coffeeRev },
      { name: 'Sand & turf rentals', value: v.sandTurfRev },
      { name: 'Flag football field', value: v.flagRev },
      { name: 'Events & parties', value: v.eventsRev },
      { name: 'Retail / pro shop', value: v.retailRev },
    ].filter((d) => d.value > 0);

    // Economic impact (SFC-style: direct spending x regional multiplier)
    const visitorDays = v.impactEvents * v.impactVisitorsPerEvent * v.impactStayDays;
    const visitorSpend = visitorDays * v.impactSpendPerDay;
    const visitorOutput = visitorSpend * v.impactMultiplier;
    const jobsDirect = v.payroll / 40000;
    const jobsTotal = jobsDirect * 1.35;
    const tenYearOutput = visitorOutput * 9.5; // ramp: 0.65 + 0.85 + 1.0 x 8
    const memberVisits = v.strengthMembers * v.memberVisitsPerWeek * v.courtWeeks;
    const programVisits = v.programParticipantsPerWeek * v.programWeeks;
    const cafeTransactions = v.cafeAvgTicket > 0 ? v.coffeeRev / v.cafeAvgTicket : 0;
    const footTraffic = memberVisits + programVisits + visitorDays + cafeTransactions;

    return {
      isLease, projectCost, equity, loanPrincipal, annualDebt, annualRent, occupancyCost,
      strengthRev, courtRev, grossRevenue, cogs, grossMargin, opex,
      ebitda, cashFlow, ebitdaMargin, coverage, cashOnCash, ramp, revenueMix,
      visitorDays, visitorSpend, visitorOutput,
      jobsTotal, tenYearOutput, footTraffic,
    };
  }, [v]);

  const sign = (n) => (n >= 0 ? 'pos' : 'neg');
  const coverageClass = m.coverage >= 1.25 ? 'pos' : m.coverage >= 1 ? 'warn' : 'neg';

  return (
    <div className="proforma-page">
      <SEO
        title="Fieldhouse Pro Forma"
        description="Interactive financial model for the proposed BEGIN Strength Fieldhouse — adjust assumptions and see revenue, EBITDA, and cash flow update live."
        path="/pro-forma"
      />
      <Header />
      <main className="pf-main">
        <header className="pf-hero">
          <h1>BEGIN Strength Fieldhouse <span className="pf-accent">Pro Forma</span></h1>
          <p>
            An interactive financial model for the proposed adaptive reuse of the former Bar K property.
            Adjust any assumption below and every output recalculates instantly. Figures are illustrative
            planning assumptions, not a guarantee of performance.
          </p>
        </header>

        <section className="pf-stats">
          <div className="pf-stat"><div className="pf-stat-value">{money(m.grossRevenue)}</div><div className="pf-stat-label">Gross Revenue (stabilized)</div></div>
          <div className="pf-stat"><div className={`pf-stat-value ${sign(m.ebitda)}`}>{money(m.ebitda)}</div><div className="pf-stat-label">EBITDA</div></div>
          <div className="pf-stat"><div className="pf-stat-value">{pct(m.ebitdaMargin)}</div><div className="pf-stat-label">EBITDA Margin</div></div>
          <div className="pf-stat"><div className={`pf-stat-value ${sign(m.cashFlow)}`}>{money(m.cashFlow)}</div><div className="pf-stat-label">Cash Flow After Occupancy</div></div>
          <div className="pf-stat"><div className={`pf-stat-value ${coverageClass}`}>{m.coverage.toFixed(2)}x</div><div className="pf-stat-label">{m.isLease ? 'Coverage (rent + TI)' : 'DSCR'}</div></div>
        </section>

        <div className={`pf-callout ${m.cashFlow >= 0 ? 'ok' : 'warn'}`}>
          {m.isLease ? 'Lease' : 'Buy'} scenario: occupancy cost is {money(m.occupancyCost)}/yr
          ({m.isLease ? `${money(m.annualRent)} rent + ${money(m.annualDebt)} build-out financing` : 'mortgage debt service'}).
          Lenders/landlords look for coverage (EBITDA &divide; occupancy cost) of at least 1.25x; this returns {m.coverage.toFixed(2)}x
          and {pct(m.cashOnCash)} cash-on-cash on {money(m.equity)} of equity.
        </div>

        <section className="pf-section">
          <div className="pf-section-head">
            <h2>Assumptions</h2>
            <button className="pf-reset" onClick={reset}>Reset to defaults</button>
          </div>

          <div className="pf-panel">
            <div className="pf-panel-head">
              <h3>{m.isLease ? 'Lease & Build-out' : 'Acquisition & Financing'}</h3>
              <span>{money(m.occupancyCost)} / yr occupancy</span>
            </div>
            <div className="pf-toggle">
              <button className={!m.isLease ? 'active' : ''} onClick={() => set('occupancy', 'buy')}>Buy ($5.5M)</button>
              <button className={m.isLease ? 'active' : ''} onClick={() => set('occupancy', 'lease')}>Lease ($25/sq ft)</button>
            </div>
            <div className="pf-grid">
              {m.isLease ? (
                <>
                  <Field label="Lease rate" name="leaseRatePerSqft" value={v.leaseRatePerSqft} onChange={set} prefix="$" suffix="/sf/yr" />
                  <Field label="Building size" name="buildingSqft" value={v.buildingSqft} onChange={set} suffix="sq ft" />
                  <Field label="Build-out & equipment (TI)" name="buildout" value={v.buildout} onChange={set} prefix="$" />
                  <Field label="Down payment (on TI)" name="downPct" value={v.downPct} onChange={set} suffix="%" />
                  <Field label="Interest rate" name="rate" value={v.rate} onChange={set} suffix="%" />
                  <Field label="Loan term" name="termYears" value={v.termYears} onChange={set} suffix="yrs" />
                </>
              ) : (
                <>
                  <Field label="Building purchase" name="purchase" value={v.purchase} onChange={set} prefix="$" />
                  <Field label="Buildout & equipment" name="buildout" value={v.buildout} onChange={set} prefix="$" />
                  <Field label="Down payment" name="downPct" value={v.downPct} onChange={set} suffix="%" />
                  <Field label="Interest rate" name="rate" value={v.rate} onChange={set} suffix="%" />
                  <Field label="Loan term" name="termYears" value={v.termYears} onChange={set} suffix="yrs" />
                </>
              )}
            </div>
            <div className="pf-subtotals">
              {m.isLease ? (
                <>
                  <span>Annual rent <strong>{money(m.annualRent)}</strong></span>
                  <span>TI financed <strong>{money(m.loanPrincipal)}</strong></span>
                  <span>TI debt service <strong>{money(m.annualDebt)}</strong></span>
                </>
              ) : (
                <>
                  <span>All-in project cost <strong>{money(m.projectCost)}</strong></span>
                  <span>Equity (down) <strong>{money(m.equity)}</strong></span>
                  <span>Loan principal <strong>{money(m.loanPrincipal)}</strong></span>
                </>
              )}
            </div>
          </div>

          <div className="pf-panel">
            <div className="pf-panel-head"><h3>Revenue Drivers</h3><span>{money(m.grossRevenue)} gross</span></div>
            <div className="pf-grid">
              <Field label="Strength members (#)" name="strengthMembers" value={v.strengthMembers} onChange={set} />
              <Field label="Monthly dues / member" name="strengthDues" value={v.strengthDues} onChange={set} prefix="$" />
              <Field label="Indoor courts (#)" name="courts" value={v.courts} onChange={set} />
              <Field label="Blended court rate" name="courtRate" value={v.courtRate} onChange={set} prefix="$" suffix="/hr" />
              <Field label="Booked hrs / court / wk" name="courtHrs" value={v.courtHrs} onChange={set} />
              <Field label="Active weeks / yr" name="courtWeeks" value={v.courtWeeks} onChange={set} />
              <Field label="Youth/family programming" name="programmingRev" value={v.programmingRev} onChange={set} prefix="$" />
              <Field label="TMRW Coffee (gross)" name="coffeeRev" value={v.coffeeRev} onChange={set} prefix="$" />
              <Field label="Coffee COGS" name="coffeeCogsPct" value={v.coffeeCogsPct} onChange={set} suffix="%" />
              <Field label="Sand & turf rentals" name="sandTurfRev" value={v.sandTurfRev} onChange={set} prefix="$" />
              <Field label="Flag football field" name="flagRev" value={v.flagRev} onChange={set} prefix="$" />
              <Field label="Events & parties" name="eventsRev" value={v.eventsRev} onChange={set} prefix="$" />
              <Field label="Retail / pro shop" name="retailRev" value={v.retailRev} onChange={set} prefix="$" />
              <Field label="Retail COGS" name="retailCogsPct" value={v.retailCogsPct} onChange={set} suffix="%" />
            </div>
          </div>

          <div className="pf-panel">
            <div className="pf-panel-head"><h3>Operating Costs (annual)</h3><span>{money(m.opex)} total</span></div>
            <div className="pf-grid">
              <Field label="Payroll (all staff)" name="payroll" value={v.payroll} onChange={set} prefix="$" />
              <Field label="Utilities" name="utilities" value={v.utilities} onChange={set} prefix="$" />
              <Field label="Insurance" name="insurance" value={v.insurance} onChange={set} prefix="$" />
              <Field label="Marketing" name="marketing" value={v.marketing} onChange={set} prefix="$" />
              <Field label="Maintenance & reserves" name="maintenance" value={v.maintenance} onChange={set} prefix="$" />
              <Field label="Software, tax, supplies, misc" name="otherOpex" value={v.otherOpex} onChange={set} prefix="$" />
            </div>
          </div>
        </section>

        <section className="pf-section">
          <h2>Projected Performance</h2>
          <div className="pf-charts">
            <div className="pf-chart-card">
              <h3>3-Year Ramp to Stabilization</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={m.ramp} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="year" />
                  <YAxis tickFormatter={moneyK} width={60} />
                  <Tooltip formatter={(val) => money(val)} />
                  <Legend />
                  <ReferenceLine y={0} stroke="#999" strokeDasharray="4 4" />
                  <Line type="monotone" dataKey="EBITDA" stroke="#16a34a" strokeWidth={2} dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="Cash Flow" stroke="#1677ff" strokeWidth={2} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
              <p className="pf-note">Revenue fill: 65% &rarr; 85% &rarr; 100% of stabilized. COGS scales with revenue; operating costs held fixed.</p>
            </div>

            <div className="pf-chart-card">
              <h3>Revenue Mix (stabilized)</h3>
              <ResponsiveContainer width="100%" height={360}>
                <PieChart>
                  <Pie data={m.revenueMix} dataKey="value" nameKey="name" cx="50%" cy="42%" innerRadius={55} outerRadius={90} paddingAngle={1}>
                    {m.revenueMix.map((entry, i) => (
                      <Cell key={entry.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(val) => money(val)} />
                  <Legend
                    layout="horizontal"
                    align="center"
                    verticalAlign="bottom"
                    iconSize={9}
                    wrapperStyle={{ fontSize: '0.72rem', lineHeight: '1.4', paddingTop: '0.5rem' }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <p className="pf-note">Share of {money(m.grossRevenue)} gross revenue by stream.</p>
            </div>
          </div>
        </section>

        <section className="pf-section">
          <h2>Stabilized P&amp;L (Year 3)</h2>
          <div className="pf-table-wrap">
            <table className="pf-table">
              <thead>
                <tr><th>Line item</th><th className="num">Annual</th><th>Notes</th></tr>
              </thead>
              <tbody>
                <tr><td>Strength memberships</td><td className="num">{money(m.strengthRev)}</td><td>{v.strengthMembers} @ {money(v.strengthDues)}/mo</td></tr>
                <tr><td>Indoor court rentals</td><td className="num">{money(m.courtRev)}</td><td>{v.courts} courts &middot; {v.courtHrs} hrs/wk &middot; {money(v.courtRate)}/hr</td></tr>
                <tr><td>Youth/family programming</td><td className="num">{money(v.programmingRev)}</td><td>Clinics, camps, lessons, memberships</td></tr>
                <tr><td>TMRW Coffee (gross)</td><td className="num">{money(v.coffeeRev)}</td><td>Built-in cafe F&amp;B</td></tr>
                <tr><td>Sand & turf rentals</td><td className="num">{money(v.sandTurfRev)}</td><td>Seasonal</td></tr>
                <tr><td>Flag football field</td><td className="num">{money(v.flagRev)}</td><td>Team/league rentals</td></tr>
                <tr><td>Events & parties</td><td className="num">{money(v.eventsRev)}</td><td>Tournaments, corporate, birthdays</td></tr>
                <tr><td>Retail / pro shop</td><td className="num">{money(v.retailRev)}</td><td>Beans, gear, apparel</td></tr>
                <tr className="pf-row-total"><td>Gross revenue</td><td className="num">{money(m.grossRevenue)}</td><td></td></tr>
                <tr><td>Less: COGS</td><td className="num">({money(m.cogs)})</td><td>F&amp;B + retail</td></tr>
                <tr className="pf-row-total"><td>Gross margin</td><td className="num">{money(m.grossMargin)}</td><td></td></tr>
                <tr><td>Less: operating expenses</td><td className="num">({money(m.opex)})</td><td>Payroll, utilities, insurance, etc.</td></tr>
                <tr className="pf-row-strong"><td>EBITDA</td><td className={`num ${sign(m.ebitda)}`}>{money(m.ebitda)}</td><td>{pct(m.ebitdaMargin)} margin</td></tr>
                {m.isLease ? (
                  <>
                    <tr><td>Less: lease (rent)</td><td className="num">({money(m.annualRent)})</td><td>{money(v.leaseRatePerSqft)}/sf &middot; {Number(v.buildingSqft).toLocaleString('en-US')} sq ft</td></tr>
                    <tr><td>Less: build-out financing</td><td className="num">({money(m.annualDebt)})</td><td>{v.rate}% &middot; {v.termYears} yr</td></tr>
                  </>
                ) : (
                  <tr><td>Less: debt service</td><td className="num">({money(m.annualDebt)})</td><td>{v.rate}% &middot; {v.termYears} yr</td></tr>
                )}
                <tr className="pf-row-strong"><td>Pre-tax cash flow</td><td className={`num ${sign(m.cashFlow)}`}>{money(m.cashFlow)}</td><td>{m.coverage.toFixed(2)}x coverage</td></tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="pf-section">
          <h2>Economic Impact (Illustrative Estimate)</h2>
          <p>
            Built with the same methodology institutional advisors like{' '}
            <a href="https://1835841.fs1.hubspotusercontent-na1.net/hubfs/1835841/Gadsen_CaseStudy_2026.pdf">The Sports Facilities Companies use for venues such as the Gadsden Athletic Center</a>{' '}
            (economic output = direct visitor and construction spending &times; a regional multiplier), but scaled to this
            facility's actual size and programming. These are planning estimates, not a formal study.
          </p>
          <div className="pf-panel">
            <div className="pf-panel-head"><h3>Impact Assumptions</h3><span>{count(m.visitorDays)} visitor-days / yr</span></div>
            <div className="pf-grid">
              <Field label="Tournament/camp weekends / yr" name="impactEvents" value={v.impactEvents} onChange={set} />
              <Field label="Non-local visitors / event" name="impactVisitorsPerEvent" value={v.impactVisitorsPerEvent} onChange={set} />
              <Field label="Avg length of stay" name="impactStayDays" value={v.impactStayDays} onChange={set} suffix="days" />
              <Field label="Spend / visitor / day" name="impactSpendPerDay" value={v.impactSpendPerDay} onChange={set} prefix="$" />
              <Field label="Regional output multiplier" name="impactMultiplier" value={v.impactMultiplier} onChange={set} suffix="x" />
              <Field label="Member visits / week" name="memberVisitsPerWeek" value={v.memberVisitsPerWeek} onChange={set} />
              <Field label="Program participants / week" name="programParticipantsPerWeek" value={v.programParticipantsPerWeek} onChange={set} />
              <Field label="Program weeks / yr" name="programWeeks" value={v.programWeeks} onChange={set} />
              <Field label="Avg cafe ticket" name="cafeAvgTicket" value={v.cafeAvgTicket} onChange={set} prefix="$" />
            </div>
          </div>
          <div className="pf-table-wrap">
            <table className="pf-table">
              <thead>
                <tr><th>OKC Fieldhouse &mdash; estimated impact</th><th className="num">Estimate</th><th>Basis</th></tr>
              </thead>
              <tbody>
                <tr><td>Annual out-of-town visitor spending (new money)</td><td className="num">~{moneyCompact(m.visitorSpend)}</td><td>{count(m.visitorDays)} visitor-days &times; {money(v.impactSpendPerDay)}/day</td></tr>
                <tr><td>Annual total economic output</td><td className="num">~{moneyCompact(m.visitorOutput)}</td><td>Visitor spend &times; {v.impactMultiplier}x multiplier</td></tr>
                <tr><td>Jobs supported (direct + indirect)</td><td className="num">~{count(m.jobsTotal)}</td><td>Coaching, ops, cafe, maintenance</td></tr>
                <tr><td>10-year cumulative output (visitor-driven, ramped)</td><td className="num">~{moneyCompact(m.tenYearOutput)}+</td><td>Annual output, 65% &rarr; 100% ramp</td></tr>
                <tr><td>Annual district foot traffic near RIVERSPORT</td><td className="num">~{count(Math.round(m.footTraffic / 1000) * 1000)} visits</td><td>Members, programs, events, cafe</td></tr>
              </tbody>
            </table>
          </div>
          <p className="pf-note">
            Estimate recalculates from the inputs above. Direct spending &times; a regional output multiplier, per SFC-style
            methodology &mdash; deliberately conservative for a 19,156 sq ft adaptive reuse. A formal feasibility/economic-impact
            study would refine these.
          </p>

          <h3 className="pf-traffic-title">Traffic Impact to RIVERSPORT &amp; the Boathouse District</h3>
          <div className="pf-traffic-head">
            <div className="pf-traffic-stat">
              <div className="pf-traffic-num">~{count(Math.round(m.footTraffic / 1000) * 1000)}</div>
              <div className="pf-traffic-num-label">added district visits / year</div>
            </div>
            <p className="pf-traffic-lead">
              The biggest district payoff isn't one-time construction dollars &mdash; it's <strong>repeatable weekly
              traffic</strong>. RIVERSPORT draws episodic, seasonal adventure trips; OKC Fieldhouse adds a steady,
              year-round flow of the exact families RIVERSPORT already wants &mdash; and the two feed each other.
            </p>
          </div>
          <div className="pf-traffic-grid">
            <div className="pf-traffic-card">
              <span className="pf-traffic-card-title">Year-round volume</span>
              <p>A steady stream of weekly practices, leagues, training, camps, events, and cafe visits &mdash; not just weekend or seasonal spikes.</p>
            </div>
            <div className="pf-traffic-card">
              <span className="pf-traffic-card-title">Shared customers</span>
              <p>Youth-sports families are prime RIVERSPORT customers for adventure passes, camps, parties, and team-building &mdash; cross-promotion both ways.</p>
            </div>
            <div className="pf-traffic-card">
              <span className="pf-traffic-card-title">Fills the slow calendar</span>
              <p>Training and league nights bring people in on weekdays and in the off-season, when adventure tourism is quiet.</p>
            </div>
            <div className="pf-traffic-card">
              <span className="pf-traffic-card-title">Sports-tourism overflow</span>
              <p>Out-of-town tournament families extend stays with RIVERSPORT activities, hotels, and dining &mdash; more room nights for the corridor.</p>
            </div>
          </div>
          <p className="pf-note">Methodology reference: The Sports Facilities Companies, <em>Gadsden Athletic Center Case Study</em> (2026).</p>
        </section>

        <section className="pf-section pf-sources">
          <h3>Benchmark sources</h3>
          <p>
            Court rates $48&ndash;$220/court/hr (<a href="https://rrsportscenter.com/facility-reservations/">Round Rock</a>,{' '}
            <a href="https://branchburgsports.com/sports-facility-rentals/indoor-court-rentals-nj/">Branchburg</a>,{' '}
            <a href="https://www.sportshouse.us/rentals">SportsHouse</a>). Gym revenue $50&ndash;$300/sq ft;
            EBITDA ~$20&ndash;24/sq ft (<a href="https://pulserevops.com/industry-kpis/ik0316">Pulse Industry KPIs</a>).
            Comparable institutional pro forma:{' '}
            <a href="https://sportsfacilities.com/blog/revenue-reality-check-benchmarking-your-facility-against-the-best/">The Sports Facilities Companies</a>{' '}
            / SFA DVL Sports Complex study (stabilized Yr-5 EBITDA +$367k).
          </p>
        </section>
      </main>
      <Footer />
    </div>
  );
}

export default ProForma;
